/* Data contracts over the real API. No X-Lumiq-Tenant header, here or anywhere — hard rule 1.
 *
 * **This was read-only, on the argument that "a screen offering a Sign button to whoever is logged in
 * would misstate what a signature is".** The concern is right and the conclusion was wrong: the
 * server refuses a signature from anyone but the declared subject for that side with a 403 — *even
 * with every permission* — and the approval kernel refuses the requester's own signature and a
 * duplicate signer on top. So "both sides signed" provably means two distinct humans, each the named
 * party. A button cannot misstate that; it can only be refused.
 *
 * What the screen owes instead is the *offer*: the control appears only for a caller who holds the
 * permission, and the row says which named party still owes a signature rather than showing a dash.
 * Withholding the button entirely left the one workflow this module exists for reachable only by
 * curl.
 *
 * The permissions split cleanly and the screen follows them: `data-steward` creates, submits and
 * evaluates; `approver` signs, activates and retires. Neither role can do the other's half, which is
 * the author/approve separation `ApprovalPolicy(eyes=2)` exists to hold.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import { isContract, isEvaluationOutcome } from '@/shared/api/generated/contracts';
import type {
  Contract,
  ContractWrite,
  EvaluationOutcome,
  SignoffSide,
} from '@/shared/api/generated/contracts';

import {
  forgetIdempotencyKey,
  getOne,
  getPage,
  idempotencyKeyFor,
  post,
} from '@/shared/api/http';
import type { Page } from '@/shared/api/http';

export const CONTRACTS_URL = '/v1/contracts';

const CONTRACTS_KEY = ['contracts', 'list'] as const;

/* The transport is `@/shared/api/http`. The local re-implementation this replaced unwrapped the
 * envelope and guarded every item exactly as `getPage` does — and omitted the bearer token, so this
 * screen 401'd under ADR-012's local auth. The refuse-the-whole-page rule below is `getPage`'s own,
 * so the behaviour is unchanged and the reasoning now lives in one place. */
/* No client-side sort. The route is already `ORDER BY created_at DESC`, and a second sort here
 * would be a second answer to "what order is this ledger in" — the two would disagree the day
 * either changed, and the server's is the one that also decides what a page contains. */
/** The ledger, as a page rather than a bare list.
 *
 * **The envelope's `truncated` is why this stopped returning `items` alone.** `GET /v1/contracts` is
 * `ORDER BY created_at DESC LIMIT n` with no cursor, so a tenant past the cap cannot reach the rest
 * — and the screen filters by state *in the client*, which turns "show me the drafts" into "show me
 * the drafts among the hundred most recent". Dropping `meta` here made that bound unreportable, so
 * an empty filtered view read as a statement about the estate when it was a statement about a page
 * boundary. The screen now has the fact and says it.
 *
 * **Deliberately unfiltered, and `?state=` is not sent.** The route accepts one, and sending it
 * would give the table a different source of truth from the work-for-you counts above it — two
 * answers to "how many drafts are there", which disagree the moment either query moves. One fetch,
 * one answer, and the bound stated where it applies.
 */
function fetchContracts(): Promise<Page<Contract>> {
  return getPage(CONTRACTS_URL, isContract, 'contract');
}

export function useContracts(): UseQueryResult<Page<Contract>> {
  return useQuery({ queryKey: CONTRACTS_KEY, queryFn: fetchContracts, retry: false });
}

/** Author a draft. `POST /v1/contracts`, 201.
 *
 * **The route has existed since the module shipped and nothing called it**, so the one act this
 * module exists for — writing down a promise between two parties — was reachable only by curl. That
 * is the "green does not mean wired" category at the top of the lifecycle: every later step had a
 * control and the first had none.
 *
 * **Keyed on the draft rather than minted fresh, and cleared on success.** A create has no natural
 * key at the substrate, so a POST that times out and is retried would otherwise write a second
 * contract over the same dataset with the same terms. Keying on `(dataset, name)` makes a retry the
 * same request; forgetting it on success means authoring a second contract from the same open drawer
 * is a second decision rather than a replay of the first.
 */
export interface CreateRequest {
  readonly body: ContractWrite;
}

export function useCreateContract(): UseMutationResult<Contract, Error, CreateRequest> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ body }: CreateRequest) =>
      post(
        CONTRACTS_URL,
        idempotencyKeyFor('contract-create', `${body.dataset_id}:${body.name}`),
        isContract,
        'contract',
        body,
      ),
    onSuccess: (_result, { body }) => {
      forgetIdempotencyKey('contract-create', `${body.dataset_id}:${body.name}`);
      void client.invalidateQueries({ queryKey: CONTRACTS_KEY });
    },
  });
}

const CONTRACT_KEY = (id: string) => ['contracts', 'one', id] as const;

export function useContract(id: string | null): UseQueryResult<Contract> {
  return useQuery({
    queryKey: CONTRACT_KEY(id ?? ''),
    enabled: id !== null && id !== '',
    queryFn: () =>
      getOne(`${CONTRACTS_URL}/${encodeURIComponent(id ?? '')}`, isContract, 'contract'),
    retry: false,
  });
}

/** Every lifecycle step, as one mutation over a verb.
 *
 * One hook rather than five, because they are the same shape — a POST to a named sub-path returning
 * the contract — and five near-identical hooks is five places to forget the cache invalidation. The
 * verb is a closed union so a typo is a compile error rather than a 404 at runtime.
 *
 * Both keys are invalidated on success: the row in the ledger and the open detail read the same
 * contract, and refreshing one would leave the other asserting a state the server has moved past.
 */
export type LifecycleVerb = 'submit' | 'activate' | 'retire';

export interface LifecycleRequest {
  readonly id: string;
  readonly verb: LifecycleVerb;
  /** Required by `retire`, ignored by the others — the server's own shape. */
  readonly reason?: string;
}

export function useContractLifecycle(): UseMutationResult<
  Contract,
  Error,
  LifecycleRequest
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, verb, reason }: LifecycleRequest) =>
      post(
        `${CONTRACTS_URL}/${encodeURIComponent(id)}/${verb}`,
        idempotencyKeyFor(`contract-${verb}`, id),
        isContract,
        'contract',
        reason === undefined ? {} : { reason },
      ),
    onSuccess: (_result, { id, verb }) => {
      forgetIdempotencyKey(`contract-${verb}`, id);
      void client.invalidateQueries({ queryKey: CONTRACTS_KEY });
      void client.invalidateQueries({ queryKey: CONTRACT_KEY(id) });
    },
  });
}

export interface SignRequest {
  readonly id: string;
  readonly side: SignoffSide;
  readonly reason: string;
}

/** One side's signature.
 *
 * **Keyed on `(contract, side)` and not on the caller**, which is the honest key: the server accepts
 * one signature per side, so a retried POST through a slow network must be the same request rather
 * than a second attempt at a decision. Signing the *other* side is a different act and gets its own
 * key.
 */
export function useSignContract(): UseMutationResult<Contract, Error, SignRequest> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, side, reason }: SignRequest) =>
      post(
        `${CONTRACTS_URL}/${encodeURIComponent(id)}/signatures`,
        idempotencyKeyFor('contract-sign', `${id}:${side}`),
        isContract,
        'contract',
        { side, reason },
      ),
    onSuccess: (_result, { id, side }) => {
      forgetIdempotencyKey('contract-sign', `${id}:${side}`);
      void client.invalidateQueries({ queryKey: CONTRACTS_KEY });
      void client.invalidateQueries({ queryKey: CONTRACT_KEY(id) });
    },
  });
}

/** Evaluate one contract's terms now.
 *
 * **A fresh key every time, deliberately — the opposite of every other mutation here.** An evaluation
 * is a *measurement*, and asking for one twice is asking twice: the second reads the terms against
 * facts that may have moved since the first. Reusing a key would silently replay a stale verdict,
 * which on the one control whose whole purpose is "is this still true right now" is the worst
 * available failure.
 */
export function useEvaluateContract(): UseMutationResult<
  EvaluationOutcome,
  Error,
  string
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      post(
        `${CONTRACTS_URL}/${encodeURIComponent(id)}/evaluate`,
        `contract-evaluate-${crypto.randomUUID()}`,
        isEvaluationOutcome,
        'contract evaluation',
        {},
      ),
    onSuccess: (_result, id) => {
      /* An evaluation can open an incident and can move nothing else, so the contract is re-read
       * rather than assumed unchanged. */
      void client.invalidateQueries({ queryKey: CONTRACTS_KEY });
      void client.invalidateQueries({ queryKey: CONTRACT_KEY(id) });
    },
  });
}
